// RC-1 Reusable RCE Consumer Framework — Report bindings table.
//
// One row per ReportShell-driven operational report. Each row is DATA:
//
//   • `consumes`     — the canonical RCE field paths this report reads. These
//                      become the "context strip" preview, the audit gate,
//                      and (eventually) the snapshot freeze list.
//
//   • `seed`         — { [rcePath]: "state.dotted.path" }. Tells the framework
//                      hook where to drop each resolved value into the report's
//                      initial form state on mount. Anything not listed here
//                      stays manual.
//
//   • `headerOrder`  — preview order (mainly cosmetic) for the context strip.
//                      Defaults to the order of `consumes` when missing.
//
// The framework hook reads ONLY this table. Each report file declares the
// row, then opts in with a single line:
//
//     const rce = useReportRceWiring("cementingReport");
//     return <ReportShell {...rce} ... />;
//
// Adding a new report = one row here + one line on the page. The same row
// drives the regression gate (`framework_schema.test.cjs`) which verifies
// every rcePath listed is registered in `classification.js`.
//
// NOTE on backwards compatibility:
//   - The Kill Sheet does NOT use this table. It retains its own
//     `KILLSHEET_CONSUMES` / `KILLSHEET_SNAPSHOT_TO_INPUT` wiring for now —
//     the framework was modelled on that pattern but does not displace it.
//   - The Daily Mud Report has hand-written RCE wiring that pre-dates this
//     framework. It is NOT migrated as part of the proof-of-concept so its
//     "freeze a Drilling Fluid Document" behaviour stays intact.

export const REPORT_RCE_BINDINGS = Object.freeze({
  // ─────────────────────────────────────────────────────────────────────
  // Cementing Report — POC consumer (header + job-level engineering).
  // ─────────────────────────────────────────────────────────────────────
  cementingReport: Object.freeze({
    label: "Cementing Report",
    consumes: Object.freeze([
      "project.operator",
      "rig.name",
      "well.name",
      "well.holeSizeIn",
      "well.casingShoeMdFt",
      "well.casingShoeTvdFt",
      "mud.weightInPpg",
    ]),
    seed: Object.freeze({
      "project.operator":     "header.operator",
      "rig.name":             "header.rigName",
      "well.name":            "header.wellName",
      "well.holeSizeIn":      "job.holeSize",
      "well.casingShoeMdFt":  "job.shoeMd",
      "well.casingShoeTvdFt": "job.shoeTvd",
      "mud.weightInPpg":      "job.mudWeight",
    }),
  }),

  // ─────────────────────────────────────────────────────────────────────
  // Wave 1 — header-only inheritance. Every report below auto-fills the
  // identity columns (operator / rig / well) from the active Engineering
  // Session. Body fields stay manual. Each row only seeds the fields the
  // report's JSX actually renders — no orphan keys added to state.
  // ─────────────────────────────────────────────────────────────────────
  tripSheet: Object.freeze({
    label: "Trip Sheet",
    consumes: Object.freeze(["rig.name", "well.name"]),
    seed: Object.freeze({
      "rig.name":  "header.rigName",
      "well.name": "header.wellName",
    }),
  }),

  lotFitReport: Object.freeze({
    label: "LOT / FIT Report",
    consumes: Object.freeze([
      "project.operator", "rig.name", "well.name",
      "well.casingShoeTvdFt", "well.holeSizeIn", "mud.weightInPpg",
    ]),
    seed: Object.freeze({
      "project.operator":     "header.operator",
      "rig.name":             "header.rigName",
      "well.name":            "header.wellName",
      "well.casingShoeTvdFt": "setup.shoeTvd",
      "well.holeSizeIn":      "setup.holeSize",
      "mud.weightInPpg":      "setup.mudWeightPpg",
    }),
  }),

  bitRunRecord: Object.freeze({
    label: "Bit Run Record",
    consumes: Object.freeze([
      "project.operator", "rig.name", "well.name",
      "mud.weightInPpg",
    ]),
    seed: Object.freeze({
      "project.operator": "header.operator",
      "rig.name":         "header.rigName",
      "well.name":        "header.wellName",
      "mud.weightInPpg":  "params.mudWeight",
    }),
  }),

  dailyOperationsSummary: Object.freeze({
    label: "Daily Operations Summary",
    consumes: Object.freeze(["project.operator", "rig.name", "well.name"]),
    seed: Object.freeze({
      "project.operator": "header.operator",
      "rig.name":         "header.rigName",
      "well.name":        "header.wellName",
    }),
  }),

  downtimeReport: Object.freeze({
    label: "Downtime Report",
    consumes: Object.freeze(["project.operator", "rig.name", "well.name"]),
    seed: Object.freeze({
      "project.operator": "header.operator",
      "rig.name":         "header.rigName",
      "well.name":        "header.wellName",
    }),
  }),

  equipmentFailureReport: Object.freeze({
    label: "Equipment Failure Report",
    consumes: Object.freeze(["project.operator", "rig.name", "well.name"]),
    seed: Object.freeze({
      "project.operator": "header.operator",
      "rig.name":         "header.rigName",
      "well.name":        "header.wellName",
    }),
  }),

  hseIncidentForm: Object.freeze({
    label: "HSE Incident Form",
    consumes: Object.freeze(["project.operator", "rig.name", "well.name"]),
    seed: Object.freeze({
      "project.operator": "header.operator",
      "rig.name":         "header.rigName",
      "well.name":        "header.wellName",
    }),
  }),

  handoverNotes: Object.freeze({
    label: "Handover Notes",
    consumes: Object.freeze(["project.operator", "rig.name", "well.name"]),
    seed: Object.freeze({
      "project.operator": "header.operator",
      "rig.name":         "header.rigName",
      "well.name":        "header.wellName",
    }),
  }),

  preJobBriefing: Object.freeze({
    label: "Pre-Job Briefing",
    consumes: Object.freeze(["rig.name"]),
    seed: Object.freeze({
      "rig.name": "header.rigName",
    }),
  }),

  preTourBriefing: Object.freeze({
    label: "Pre-Tour Briefing",
    consumes: Object.freeze(["rig.name", "well.name"]),
    seed: Object.freeze({
      "rig.name":  "header.rigName",
      "well.name": "header.wellName",
    }),
  }),

  toolboxTalk: Object.freeze({
    label: "Toolbox Talk",
    consumes: Object.freeze(["rig.name", "well.name"]),
    seed: Object.freeze({
      "rig.name":  "header.rigName",
      "well.name": "header.wellName",
    }),
  }),

  jsa: Object.freeze({
    label: "JSA / Job Safety Analysis",
    consumes: Object.freeze(["rig.name"]),
    seed: Object.freeze({
      "rig.name": "header.rigName",
    }),
  }),

  bopTestSheet: Object.freeze({
    label: "BOP Test Sheet",
    consumes: Object.freeze(["project.operator", "rig.name", "well.name"]),
    seed: Object.freeze({
      "project.operator": "header.operator",
      "rig.name":         "header.rigName",
      "well.name":        "header.wellName",
    }),
  }),

  surfaceBopTestReport: Object.freeze({
    label: "Surface BOP Test Report",
    consumes: Object.freeze(["project.operator", "rig.name", "well.name"]),
    seed: Object.freeze({
      "project.operator": "header.operator",
      "rig.name":         "header.rigName",
      "well.name":        "header.wellName",
    }),
  }),

  subseaBopTestReport: Object.freeze({
    label: "Subsea BOP Test Report",
    consumes: Object.freeze(["project.operator", "rig.name", "well.name"]),
    seed: Object.freeze({
      "project.operator": "header.operator",
      "rig.name":         "header.rigName",
      "well.name":        "header.wellName",
    }),
  }),

  // KickSheet.jsx is intentionally OUT of the framework's reach.
  // It's the shared component backing the 6 legacy Surface/Subsea kick-sheet
  // routes that RC-1 has already slated for quarantine (Phase 3 of the
  // implementation plan). Migrating it would couple the framework to
  // deprecated code that will be removed in V2.

  // ─────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────
  // Reports NOT migrated here (intentional — backwards compatibility):
  //
  //   • dailyDrillingReport  — has bespoke RCE wiring pre-dating the
  //                            framework (writes back to lib/dailyOps).
  //   • dailyMudReport       — same; persists a parallel Drilling Fluid
  //                            Document via onAfterSave.
  //
  // Both will be migrated to the framework in a follow-up pass that also
  // generalises the onAfterSave snapshot-freeze action. Skipping them now
  // keeps the engineering-snapshot persistence path untouched.
  // ─────────────────────────────────────────────────────────────────────
});

/** Read a single binding row. Returns `null` if the key is unknown so a
 *  consumer that calls `useReportRceWiring("typo")` degrades gracefully
 *  instead of throwing inside the React render tree. */
export function getReportBinding(reportKey) {
  if (!reportKey) return null;
  return REPORT_RCE_BINDINGS[reportKey] || null;
}

/** Lists every registered report key — used by the regression gate. */
export function listReportBindings() {
  return Object.keys(REPORT_RCE_BINDINGS);
}
